﻿import * as THREE from 'three';
import {AbstractPointSnapStrategy} from './AbstractPointSnapStrategy';
import {PointSnapStrategyType} from './PointSnapStrategyType';
import type {PointSnapRequest, PointSnapResult} from './PointSnapTypes';
import {
    LineMaterialFactory,
    type ScreenSpaceDashedLineNodeMaterial,
    syncScreenSpaceDashedLineGeometry,
} from '../../material';
import {isSketchCircleObject} from '../../mesh/guards/isSketchCircleObject';
import {isSketchArcObject} from '../../mesh/guards/isSketchArcObject';
import {isSketchEllipseObject} from '../../mesh/guards/isSketchEllipseObject';
import type {AbstractSketchEdgeObject} from '../../mesh/sketch/AbstractSketchEdgeObject';
import {
    computeCircleTangentTouchPointsLocal,
    computeArcTangentTouchPointsLocal,
    computeEllipseTangentTouchPointsLocal,
    closestPointOnCircleLocal,
    closestPointOnArcLocal,
    closestPointOnEllipseLocal,
    tangentDirectionOnCircleLocal,
    tangentDirectionOnEllipseLocal,
} from '../tangent/TangentLineComputer';
import {computeEdgeScreenDistance} from '../utils/ScreenSpaceUtils';
import type {ReferencePlane} from '../../reference/ReferencePlane';
import {QuickSnapMode} from './QuickSnapMode';
import {GeometryInteractionState} from '../interactionState/GeometryInteractionState';
import {scheduleDisposableResourceDisposal} from '../../utils';
import type {SketchCircleObject} from '../../mesh/sketch/SketchCircleObject';
import type {SketchArcObject} from '../../mesh/sketch/SketchArcObject';
import type {SketchEllipseObject} from '../../mesh/sketch/SketchEllipseObject';



/**
 * 相切捕捉支持的源曲线类型。
 */
type TangentGuideTarget = SketchCircleObject | SketchArcObject | SketchEllipseObject;

/**
 * 判断对象是否支持普通相切捕捉。
 *
 * @param object 待判断对象。
 * @returns 支持相切捕捉时返回 true。
 */
function isTangentGuideTarget(object: THREE.Object3D): object is TangentGuideTarget {
    return isSketchCircleObject(object) || isSketchArcObject(object) || isSketchEllipseObject(object);
}

/**
 * 判断相切捕捉源曲线是否为绘制预览对象。
 *
 * @param target 相切捕捉源曲线。
 * @returns 源曲线是绘制预览对象时返回 true。
 */
function isTangentPreviewTarget(target: TangentGuideTarget): boolean {
    if (isSketchArcObject(target)) {
        return target.isDrawingPreviewArc;
    }

    return false;
}

/**
 * 相切捕捉策略。
 * 职责：
 * 1. 普通模式：检测鼠标悬停的圆/弧/椭圆边缘；计算从绘制起点到该曲线的切点，
 *    构建引导线；当鼠标方向与切线对齐时返回切点作为捕捉结果。
 * 2. 快速相切捕捉模式（QuickSnapMode.Tangent）：
 *    - 阶段一（无 drawingFirstPoint）：将第一个点直接吸附到曲线上，
 *      高亮曲线，预览点随鼠标在曲线上滑动；
 *    - 阶段二（有 drawingFirstPoint，该点在曲线上）：计算曲线在该点处的切线，
 *      将鼠标约束到切线方向，确保绘制出的线段与曲线相切。
 */
export class TangentPointSnapStrategy extends AbstractPointSnapStrategy {
    /** 策略类型。 */
    public readonly strategyType: PointSnapStrategyType = PointSnapStrategyType.Tangent;

    /** 切线捕捉屏幕像素阈值。 */
    private static readonly TANGENT_SCREEN_DISTANCE_THRESHOLD_PX = 5;

    /** 快速捕捉模式权重，确保必定胜出所有普通策略。 */
    private static readonly QUICK_SNAP_WEIGHT = 200;

    /**
     * 切线预览线信息列表（普通模式使用）。
     * 说明：同时记录 THREE.Line 对象、归一化方向向量和切点世界坐标，
     * 用于每帧检测预览方向是否与切线方向重合以吸附并切换高亮材质。
     */
    private tangentPreviewLineInfos: Array<{
        /** 切线渲染对象。 */
        line: THREE.Line;
        /** 从起点到切点的归一化方向向量（世界坐标）。 */
        direction: THREE.Vector3;
        /** 切点世界坐标，吸附时使用。 */
        touchWorld: THREE.Vector3;
        /** 普通状态屏幕空间虚线材质。 */
        normalMaterial: ScreenSpaceDashedLineNodeMaterial;
        /** 高亮状态屏幕空间虚线材质。 */
        highlightMaterial: ScreenSpaceDashedLineNodeMaterial;
    }> = [];

    /** 上一次生成切线的目标形状对象（普通模式），用于判断是否需要重建切线。 */
    private lastTargetObject: THREE.Object3D | null = null;

    
    /** 快速捕捉高亮的边线（阶段一/阶段二共用）。 */
    private quickSnapHighlightedEdge: AbstractSketchEdgeObject | null = null;

    /**
     * 快速捕捉阶段二的切线引导线。
     * 说明：在 drawingFirstPoint 确定后，显示该点在曲线上的切线方向。
     */
    private quickSnapTangentLine: THREE.Line | null = null;

    /**
     * 快速捕捉阶段二记录的曲线形状。
     * 说明：用于在阶段二重新计算切线方向，保证 drawingFirstPoint 不变时切线始终正确。
     */
    private quickSnapLockedShape: AbstractSketchEdgeObject | null = null;

    /**
     * 快速捕捉阶段二的切线方向（归一化世界坐标向量）。
     * 说明：由 drawingFirstPoint 与 quickSnapLockedShape 共同决定，每帧更新。
     */
    private quickSnapTangentDirection: THREE.Vector3 | null = null;
    

    /**
     * 执行相切捕捉。
     * @param request 点捕捉请求参数（包含 drawingFirstPoint）。
     * @returns 捕捉结果；未命中时返回 null。
     */
    public resolve(request: PointSnapRequest): PointSnapResult | null {
        
        if (request.quickSnapMode === QuickSnapMode.Tangent) {
            return this.resolveQuickSnap(request);
        }
        

        // 普通模式：清除快速捕捉残留。
        this.clearQuickSnapState();

        const firstPoint = request.drawingFirstPoint ?? null;
        // 无绘制起点时清除所有切线预览。
        if (!firstPoint) {
            this.clearPreview();
            return null;
        }

        const { screenPosition: screenPos, worldPosition: rawPoint } = request;

        // 检测悬停的曲线边（圆/弧/椭圆）。
        const hoveredShape = this.findHoveredCurveShape(request);

        // 悬停形状变化时清除旧切线并重建新切线。
        if (hoveredShape && hoveredShape !== this.lastTargetObject) {
            this.clearPreview();
            this.lastTargetObject = hoveredShape;
            this.buildTangentLines(firstPoint, hoveredShape);
        }

        // 每帧更新高亮状态（无论是否重建切线）。
        this.updateHighlightState(firstPoint, rawPoint, screenPos);

        // 检测是否与某切线方向对齐，对齐时返回投影点作为捕捉结果。
        const snapPoint = this.getSnapPreviewPoint(firstPoint, rawPoint, screenPos);
        if (snapPoint) {
            return {
                strategyType: this.strategyType,
                position: snapPoint,
                indicatorPosition: snapPoint,
                targetObject: null,
                weight: 1000,
            };
        }
        return null;
    }

    /**
     * 清除所有切线引导线（普通模式）和快速捕捉高亮，释放资源。
     */
    public override clearPreview(): void {
        // 清除普通模式切线。
        if (this.tangentPreviewLineInfos.length > 0) {
            for (const info of this.tangentPreviewLineInfos) {
                this.scene?.remove(info.line);
                scheduleDisposableResourceDisposal(info.line.geometry);
                scheduleDisposableResourceDisposal(info.normalMaterial);
                scheduleDisposableResourceDisposal(info.highlightMaterial);
            }
            this.tangentPreviewLineInfos = [];
            this.lastTargetObject = null;
        }
        // 清除快速捕捉状态。
        this.clearQuickSnapState();
    }

    

    /**
     * 快速相切捕捉主入口。
     * 阶段一（无 drawingFirstPoint）：找最近曲线，将第一个点吸附到曲线上。
     * 阶段二（有 drawingFirstPoint）：计算曲线切线，约束第二个点在切线上。
     * @param request 点捕捉请求参数。
     * @returns 捕捉结果。
     */
    private resolveQuickSnap(request: PointSnapRequest): PointSnapResult | null {
        const firstPoint = request.drawingFirstPoint ?? null;

        if (!firstPoint) {
            return this.resolveQuickSnapPhase1(request);
        } else {
            return this.resolveQuickSnapPhase2(request, firstPoint);
        }
    }

    /**
     * 快速相切捕捉阶段一：找最近曲线，将鼠标吸附到曲线上。
     * 说明：
     * - 全局搜索最近的圆/圆弧/椭圆（无距离限制）；
     * - 计算鼠标在曲线上的最近投影点；
     * - 高亮曲线边线，返回投影点作为捕捉结果（权重 200）。
     * @param request 点捕捉请求参数。
     * @returns 曲线上最近投影点的捕捉结果；无曲线时返回 null。
     */
    private resolveQuickSnapPhase1(request: PointSnapRequest): PointSnapResult | null {
        // 清除阶段二切线（模式刚激活时可能残留）。
        this.clearQuickSnapTangentLine();

        const group = this.viewer?.sketchGroupManager?.activeSketchGroup;
        if (!group) {
            this.clearQuickSnapEdgeHighlight();
            return null;
        }

        // 全局找屏幕距离最近的曲线（圆/弧/椭圆）。
        let bestShape: AbstractSketchEdgeObject | null = null;
        let bestScreenDist = Infinity;

        for (const edge of group.getSketchEdgeObjects()) {
            if (this.isSnapTargetExcluded(request, edge)) {
                continue;
            }
            if (!isSketchCircleObject(edge) && !isSketchArcObject(edge) && !isSketchEllipseObject(edge)) {
                continue;
            }
            if (isSketchArcObject(edge) && edge.isDrawingPreviewArc) {
                continue;
            }
            if (!this.viewer) continue;
            const dist = computeEdgeScreenDistance(this.viewer, edge.geometry, request.screenPosition);
            if (dist !== null && dist < bestScreenDist) {
                bestScreenDist = dist;
                bestShape = edge;
            }
        }

        if (!bestShape) {
            this.clearQuickSnapEdgeHighlight();
            return null;
        }

        // 差量高亮曲线边线。
        if (this.quickSnapHighlightedEdge !== bestShape) {
            this.clearQuickSnapEdgeHighlight();
            this.viewer?.interactionStateManager?.setSketchObjectState(
                bestShape,
                GeometryInteractionState.Hover,
            );
            this.quickSnapHighlightedEdge = bestShape;
        }

        // 记录形状供阶段二使用。
        this.quickSnapLockedShape = bestShape;

        // 计算鼠标在曲线上的最近点。
        const closestLocal = this.computeClosestPointOnShape(bestShape, request.worldPosition);
        if (!closestLocal) {
            return null;
        }
        const closestWorld = bestShape.referencePlane.localToWorld(closestLocal);

        return {
            strategyType: this.strategyType,
            position: closestWorld,
            indicatorPosition: closestWorld,
            targetObject: bestShape,
            weight: TangentPointSnapStrategy.QUICK_SNAP_WEIGHT,
            showIndicator: true,
        };
    }

    /**
     * 快速相切捕捉阶段二：曲线已锁定，动态计算切点作为线段起点（P1），鼠标位置作为终点（P2）。
     * 说明：
     * - 每帧从当前鼠标位置计算到锁定曲线的切点；
     * - 返回 position = mousePos（P2）、startPointOverride = tangentPoint（P1）；
     * - SketchDrawingManager 用 startPointOverride 覆盖 drawingPoints[0]，
     *   保证最终绘制线段 tangentPoint → mousePos 与曲线相切；
     * - 同时更新切线引导线（从切点延伸的切线方向预览）。
     * @param request 点捕捉请求参数。
     * @param firstPoint drawingFirstPoint（第一次点击时的初始切点，仅用于定位 lockedShape）。
     * @returns 捕捉结果（position=鼠标, startPointOverride=切点）。
     */
    private resolveQuickSnapPhase2(request: PointSnapRequest, firstPoint: THREE.Vector3): PointSnapResult | null {
        // 若 lockedShape 丢失（如切换了工具再回来），重新查找最近曲线。
        if (this.quickSnapLockedShape && this.isSnapTargetExcluded(request, this.quickSnapLockedShape)) {
            this.quickSnapLockedShape = null;
            this.clearQuickSnapEdgeHighlight();
        }
        if (!this.quickSnapLockedShape) {
            this.quickSnapLockedShape = this.findNearestCurveToPoint(firstPoint, request);
        }
        if (!this.quickSnapLockedShape) {
            return null;
        }

        const shape = this.quickSnapLockedShape;
        const mouseWorld = request.worldPosition;

        // 从鼠标位置计算到锁定曲线的切点列表。
        const tangentPointsLocal = this.computeTangentPointsFromMouse(shape, mouseWorld);
        if (!tangentPointsLocal || tangentPointsLocal.length === 0) {
            // 鼠标在曲线内部（无切线）：回退到曲线上最近点。
            const closestLocal = this.computeClosestPointOnShape(shape, mouseWorld);
            if (!closestLocal) return null;
            const closestWorld = shape.referencePlane.localToWorld(closestLocal);
            this.clearQuickSnapTangentLine();
            return {
                strategyType: this.strategyType,
                position: mouseWorld.clone(),
                indicatorPosition: closestWorld,
                targetObject: shape,
                weight: TangentPointSnapStrategy.QUICK_SNAP_WEIGHT,
                showIndicator: true,
                startPointOverride: closestWorld,
            };
        }

        // 多个切点时，取距离 drawingFirstPoint 最近的那个（视觉上更连续）。
        let bestTangentWorld: THREE.Vector3 = shape.referencePlane.localToWorld(tangentPointsLocal[0]);
        let bestDist = firstPoint.distanceTo(bestTangentWorld);
        for (let i = 1; i < tangentPointsLocal.length; i++) {
            const w = shape.referencePlane.localToWorld(tangentPointsLocal[i]);
            const d = firstPoint.distanceTo(w);
            if (d < bestDist) {
                bestDist = d;
                bestTangentWorld = w;
            }
        }

        // 在切点处绘制切线引导线（切点 → 鼠标），仅限制显示长度，不改变真实鼠标捕捉结果。
        this.rebuildQuickSnapTangentLine(bestTangentWorld, mouseWorld);

        return {
            strategyType: this.strategyType,
            position: mouseWorld.clone(),
            indicatorPosition: bestTangentWorld,
            targetObject: shape,
            weight: TangentPointSnapStrategy.QUICK_SNAP_WEIGHT,
            showIndicator: true,
            // 动态切点：SketchDrawingManager 将用此覆盖 drawingPoints[0]。
            startPointOverride: bestTangentWorld,
        };
    }

    /**
     * 从鼠标世界坐标计算到曲线的切点列表（局部2D坐标）。
     * @param shape 目标曲线对象。
     * @param mouseWorld 鼠标世界坐标。
     * @returns 切点局部2D坐标数组；鼠标在曲线内时返回空数组。
     */
    private computeTangentPointsFromMouse(
        shape: AbstractSketchEdgeObject,
        mouseWorld: THREE.Vector3,
    ): THREE.Vector2[] | null {
        const rp = shape.referencePlane;
        const mouseLocal = rp.worldToLocal(mouseWorld);

        if (isSketchCircleObject(shape)) {
            const center = rp.worldToLocal(shape.getCenterWorld());
            return computeCircleTangentTouchPointsLocal(mouseLocal, center, shape.getRadius());
        }
        if (isSketchArcObject(shape)) {
            const def = shape.getArcDefinition();
            const center = rp.worldToLocal(def.centerWorld);
            return computeArcTangentTouchPointsLocal(mouseLocal, center, def.radius, def.startAngle, def.endAngle, def.clockwise);
        }
        if (isSketchEllipseObject(shape)) {
            const def = shape.getEllipseDefinition();
            const center = rp.worldToLocal(def.centerWorld);
            return computeEllipseTangentTouchPointsLocal(mouseLocal, center, def.radiusX, def.radiusY, def.rotation);
        }
        return null;
    }

    /**
     * 计算鼠标世界坐标在曲线上的最近投影点（返回局部2D坐标）。
     * @param shape 目标曲线对象。
     * @param worldPos 鼠标世界坐标。
     * @returns 曲线上最近点的局部2D坐标；不支持的类型返回 null。
     */
    private computeClosestPointOnShape(
        shape: AbstractSketchEdgeObject,
        worldPos: THREE.Vector3,
    ): THREE.Vector2 | null {
        const rp = shape.referencePlane;
        const mouseLocal = rp.worldToLocal(worldPos);

        if (isSketchCircleObject(shape)) {
            const center = rp.worldToLocal(shape.getCenterWorld());
            return closestPointOnCircleLocal(mouseLocal, center, shape.getRadius());
        }
        if (isSketchArcObject(shape)) {
            const def = shape.getArcDefinition();
            const center = rp.worldToLocal(def.centerWorld);
            return closestPointOnArcLocal(mouseLocal, center, def.radius, def.startAngle, def.endAngle, def.clockwise);
        }
        if (isSketchEllipseObject(shape)) {
            const def = shape.getEllipseDefinition();
            const center = rp.worldToLocal(def.centerWorld);
            return closestPointOnEllipseLocal(mouseLocal, center, def.radiusX, def.radiusY, def.rotation);
        }
        return null;
    }

    /**
     * 计算曲线在指定世界坐标点处的切线方向（归一化世界坐标）。
     * @param shape 目标曲线对象。
     * @param worldPoint 曲线上的点（世界坐标）。
     * @returns 归一化切线方向向量；不支持的类型返回 null。
     */
    private computeTangentDirectionAtPoint(
        shape: AbstractSketchEdgeObject,
        worldPoint: THREE.Vector3,
    ): THREE.Vector3 | null {
        const rp = shape.referencePlane;
        const pointLocal = rp.worldToLocal(worldPoint);
        let dirLocal: THREE.Vector2 | null = null;

        if (isSketchCircleObject(shape)) {
            const center = rp.worldToLocal(shape.getCenterWorld());
            dirLocal = tangentDirectionOnCircleLocal(pointLocal, center);
        } else if (isSketchArcObject(shape)) {
            // 圆弧切线方向与圆相同：半径方向旋转 90°。
            const def = shape.getArcDefinition();
            const center = rp.worldToLocal(def.centerWorld);
            dirLocal = tangentDirectionOnCircleLocal(pointLocal, center);
        } else if (isSketchEllipseObject(shape)) {
            const def = shape.getEllipseDefinition();
            const center = rp.worldToLocal(def.centerWorld);
            dirLocal = tangentDirectionOnEllipseLocal(pointLocal, center, def.radiusX, def.radiusY, def.rotation);
        }

        if (!dirLocal) return null;

        // 将局部2D方向转换为世界3D方向：使用参考平面的 xAxis 和 yAxis 基向量。
        const worldDir = new THREE.Vector3()
            .addScaledVector(rp.xAxis, dirLocal.x)
            .addScaledVector(rp.yAxis, dirLocal.y)
            .normalize();
        return worldDir;
    }

    /**
     * 全局查找离指定世界坐标点最近的圆/圆弧/椭圆。
     * 说明：用于阶段二 lockedShape 丢失时恢复。
     * @param worldPoint 世界坐标点。
     * @param request 可选点捕捉请求，用于排除当前变换域对象。
     * @returns 最近曲线对象；无曲线时返回 null。
     */
    private findNearestCurveToPoint(worldPoint: THREE.Vector3, request?: PointSnapRequest): AbstractSketchEdgeObject | null {
        const group = this.viewer?.sketchGroupManager?.activeSketchGroup;
        if (!group) return null;

        let bestShape: AbstractSketchEdgeObject | null = null;
        let bestDist = Infinity;

        for (const edge of group.getSketchEdgeObjects()) {
            if (request && this.isSnapTargetExcluded(request, edge)) {
                continue;
            }
            if (!isSketchCircleObject(edge) && !isSketchArcObject(edge) && !isSketchEllipseObject(edge)) {
                continue;
            }
            const closestLocal = this.computeClosestPointOnShape(edge, worldPoint);
            if (!closestLocal) continue;
            const closestWorld = edge.referencePlane.localToWorld(closestLocal);
            const dist = worldPoint.distanceTo(closestWorld);
            if (dist < bestDist) {
                bestDist = dist;
                bestShape = edge;
            }
        }
        return bestShape;
    }

    /**
     * 创建或更新阶段二的切线预览线（切点 → 鼠标位置）。
     * 说明：阶段二不再显示无限延伸的切线，而是直接显示从切点到鼠标的线段预览。
     * @param tangentPoint 曲线上的切点（世界坐标）。
     * @param mouseWorld 鼠标世界坐标。
     */
    private rebuildQuickSnapTangentLine(tangentPoint: THREE.Vector3, mouseWorld: THREE.Vector3): void {
        const guideEndPoint = this.resolveLimitedPointSnapGuideLineEndPoint(tangentPoint, mouseWorld);
        if (this.quickSnapTangentLine) {
            // 复用几何体，直接更新顶点。
            const positions = this.quickSnapTangentLine.geometry.attributes['position'];
            if (positions) {
                positions.setXYZ(0, tangentPoint.x, tangentPoint.y, tangentPoint.z);
                positions.setXYZ(1, guideEndPoint.x, guideEndPoint.y, guideEndPoint.z);
                positions.needsUpdate = true;
                syncScreenSpaceDashedLineGeometry(this.quickSnapTangentLine);
            }
            return;
        }

        // 首次创建。
        const points = [tangentPoint, guideEndPoint];
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        const line = new THREE.Line(
            geometry,
            LineMaterialFactory.createTangentGuideHighlightMaterial(points),
        );
        syncScreenSpaceDashedLineGeometry(line);
        this.addPreviewObject(line);
        this.quickSnapTangentLine = line;
    }

    /**
     * 清除快速捕捉边线高亮，恢复默认材质。
     */
    private clearQuickSnapEdgeHighlight(): void {
        if (this.quickSnapHighlightedEdge !== null) {
            this.viewer?.interactionStateManager?.restoreSketchObjectState(this.quickSnapHighlightedEdge);
            this.quickSnapHighlightedEdge = null;
        }
    }

    /**
     * 移除阶段二切线引导线并释放几何体。
     */
    private clearQuickSnapTangentLine(): void {
        if (this.quickSnapTangentLine) {
            this.scene?.remove(this.quickSnapTangentLine);
            scheduleDisposableResourceDisposal(this.quickSnapTangentLine.geometry);
            const material = Array.isArray(this.quickSnapTangentLine.material)
                ? this.quickSnapTangentLine.material[0]
                : this.quickSnapTangentLine.material;
            scheduleDisposableResourceDisposal(material);
            this.quickSnapTangentLine = null;
        }
        this.quickSnapTangentDirection = null;
    }

    /**
     * 清除所有快速捕捉相关状态（边线高亮 + 切线引导线 + 锁定形状）。
     */
    private clearQuickSnapState(): void {
        this.clearQuickSnapEdgeHighlight();
        this.clearQuickSnapTangentLine();
        this.quickSnapLockedShape = null;
    }

    

    /**
     * 找到鼠标当前悬停的圆/圆弧/椭圆边缘对象（普通模式）。
     * @param request 点捕捉请求。
     * @returns 最近的曲线边缘对象；无悬停时返回 null。
     */
    private findHoveredCurveShape(request: PointSnapRequest): TangentGuideTarget | null {
        return this.resolveHoveredActiveSketchEdge(
            request,
            isTangentGuideTarget,
            isTangentPreviewTarget,
        );
    }

    /**
     * 根据悬停形状计算切点并构建切线引导线（普通模式）。
     * @param firstPoint 绘制起点（世界坐标）。
     * @param hoveredShape 悬停的曲线边缘对象。
     */
    private buildTangentLines(firstPoint: THREE.Vector3, hoveredShape: AbstractSketchEdgeObject): void {
        let touchPointsLocal: THREE.Vector2[] = [];
        let referencePlane: ReferencePlane | null = null;

        if (isSketchCircleObject(hoveredShape)) {
            referencePlane = hoveredShape.referencePlane;
            const fromLocal = referencePlane.worldToLocal(firstPoint);
            const centerLocal = referencePlane.worldToLocal(hoveredShape.getCenterWorld());
            touchPointsLocal = computeCircleTangentTouchPointsLocal(fromLocal, centerLocal, hoveredShape.getRadius());
        } else if (isSketchArcObject(hoveredShape)) {
            referencePlane = hoveredShape.referencePlane;
            const fromLocal = referencePlane.worldToLocal(firstPoint);
            const def = hoveredShape.getArcDefinition();
            const centerLocal = referencePlane.worldToLocal(def.centerWorld);
            touchPointsLocal = computeArcTangentTouchPointsLocal(
                fromLocal, centerLocal, def.radius, def.startAngle, def.endAngle, def.clockwise,
            );
        } else if (isSketchEllipseObject(hoveredShape)) {
            referencePlane = hoveredShape.referencePlane;
            const fromLocal = referencePlane.worldToLocal(firstPoint);
            const def = hoveredShape.getEllipseDefinition();
            const centerLocal = referencePlane.worldToLocal(def.centerWorld);
            touchPointsLocal = computeEllipseTangentTouchPointsLocal(
                fromLocal, centerLocal, def.radiusX, def.radiusY, def.rotation,
            );
        }

        if (referencePlane && touchPointsLocal.length > 0) {
            for (const touchLocal of touchPointsLocal) {
                const touchWorld = referencePlane.localToWorld(touchLocal.x, touchLocal.y);
                this.buildOneTangentLine(firstPoint, touchWorld);
            }
        }
    }

    /**
     * 根据切点构建一条长度统一的切线引导线（普通模式）。
     * @param firstPoint 绘制起点（世界坐标）。
     * @param touchWorld 切点（世界坐标）。
     */
    private buildOneTangentLine(firstPoint: THREE.Vector3, touchWorld: THREE.Vector3): void {
        const dir = new THREE.Vector3().subVectors(touchWorld, firstPoint);
        if (dir.lengthSq() < 1e-18) {
            return;
        }
        dir.normalize();

        const extend = this.pointSnapGuideLineLength;
        const ptA = firstPoint.clone().addScaledVector(dir, -extend);
        const ptB = firstPoint.clone().addScaledVector(dir, extend);

        const points = [ptA, ptB];
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        const normalMaterial = LineMaterialFactory.createTangentGuidePreviewMaterial(points);
        const highlightMaterial = LineMaterialFactory.createTangentGuideHighlightMaterial(points);
        const line = new THREE.Line(geometry, normalMaterial);
        syncScreenSpaceDashedLineGeometry(line);
        this.addPreviewObject(line);
        this.tangentPreviewLineInfos.push({
            line,
            direction: dir,
            touchWorld: touchWorld.clone(),
            normalMaterial,
            highlightMaterial,
        });
    }

    /**
     * 根据当前鼠标原始方向，更新各切线的高亮状态（普通模式）。
     * @param firstPoint 绘制起点（世界坐标）。
     * @param rawPoint 当前鼠标世界坐标（未捕捉）。
     */
    private updateHighlightState(firstPoint: THREE.Vector3, rawPoint: THREE.Vector3, screenPos: THREE.Vector2): void {
        if (this.tangentPreviewLineInfos.length === 0) {
            return;
        }

        const previewDir = new THREE.Vector3().subVectors(rawPoint, firstPoint);
        if (previewDir.lengthSq() < 1e-18) {
            return;
        }
        previewDir.normalize();

        const pixelThreshold = TangentPointSnapStrategy.TANGENT_SCREEN_DISTANCE_THRESHOLD_PX;

        for (const info of this.tangentPreviewLineInfos) {
            const projected = this.resolveProjectedLineScreenDistance(firstPoint, rawPoint, info.direction, screenPos);
            const dot = Math.abs(previewDir.dot(info.direction));
            info.line.material = projected !== null && projected.screenDistance <= pixelThreshold
                ? info.highlightMaterial
                : info.normalMaterial;
            if (dot >= 0) {
                // 保留旧逻辑的方向计算副作用结构，避免扩大非5px相关改动。
            }
        }
    }

    /**
     * 检测切线吸附时，将鼠标位置投影到吸附线上（普通模式）。
     * @param firstPoint 绘制起点（世界坐标）。
     * @param rawPoint 当前鼠标世界坐标。
     * @param screenPos 当前鼠标屏幕坐标。
     * @returns 投影点世界坐标；无吸附时返回 null。
     */
    private getSnapPreviewPoint(firstPoint: THREE.Vector3, rawPoint: THREE.Vector3, screenPos: THREE.Vector2): THREE.Vector3 | null {
        if (this.tangentPreviewLineInfos.length === 0) {
            return null;
        }

        const threshold = TangentPointSnapStrategy.TANGENT_SCREEN_DISTANCE_THRESHOLD_PX;
        let bestPoint: THREE.Vector3 | null = null;
        let bestDistance = Infinity;

        for (const info of this.tangentPreviewLineInfos) {
            const projected = this.resolveProjectedLineScreenDistance(firstPoint, rawPoint, info.direction, screenPos);
            if (!projected) {
                continue;
            }
            if (projected.screenDistance <= threshold && projected.screenDistance < bestDistance) {
                bestDistance = projected.screenDistance;
                bestPoint = projected.projectedWorld;
            }
        }
        return bestPoint;
    }
}



